import { Colors, Img, Lightning, Utils } from "@lightningjs/sdk";
import { ChannelItemModel, MediaModel, MediaType } from "../../../lib/models";
import theme from "../../../lib/theme";
import { getMediaPosterProps } from "../../../lib/utils";
import { Theme, themeManager } from "../../../components/configurations/Theme/ThemeManager";
import configs from "../../../lib/configs";

interface MediaPosterTemplateSpec extends Lightning.Component.TemplateSpec {
    data: MediaModel | ChannelItemModel;
    useBorder: boolean;
    useBackground: boolean;
    Background: {
        Poster: object;
        Placeholder: object;
        Border: object;
    };
}

export class MediaPoster
    extends Lightning.Component<MediaPosterTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MediaPosterTemplateSpec>
{
    _src: string | undefined;
    _type!: MediaType;
    _isActive = false;

    _useBackground = false;
    _useBorder = true;

    _boundEventHandler?: any;

    private _themeCallback!: (customTheme: Theme | undefined) => void;

    static override _template(): Lightning.Component.Template<MediaPosterTemplateSpec> {
        return {
            Background: {
                w: (w) => w,
                h: (h) => h,
                // rect: true,
                // clipping: true,
                // color: Colors(theme.colors.bg.secondary).get(),
                Placeholder: {
                    mount: 0.5,
                    w: Math.min(theme.mediaItem.w, theme.mediaItem.h) - 40,
                    h: Math.min(theme.mediaItem.w, theme.mediaItem.h) - 40,
                    x: (w) => w / 2,
                    y: (y) => y / 2,
                    texture: Img(Utils.asset("images/placeholder.png")).contain(
                        Math.min(theme.mediaItem.w, theme.mediaItem.h) - 40,
                        Math.min(theme.mediaItem.w, theme.mediaItem.h) - 40
                    )
                },
                Poster: {
                    w: (w) => w,
                    h: (h) => h,
                    mount: 0.5,
                    x: (w) => w / 2,
                    y: (y) => y / 2,
                    alpha: 0.01,
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: theme.mediaItem.border.radius
                    }
                },
                Border: {
                    mount: 0.5,
                    x: (w) => w / 2 + theme.mediaItem.border.w / 2 - theme.mediaItem.border.w / 2,
                    y: (y) => y / 2 + theme.mediaItem.border.w / 2 - theme.mediaItem.border.w / 2,
                    alpha: 0
                }
            }
        };
    }

    readonly Background = this.getByRef("Background")!;
    readonly Border = this.Background.getByRef("Border")!;
    readonly Poster = this.Background.getByRef("Poster")!;
    readonly Placeholder = this.Background.getByRef("Placeholder")!;

    set data(value: MediaModel | ChannelItemModel) {
        if ("coverUrl" in value) {
            this._src = value?.coverUrl;
        } else {
            this._src = value?.logo;
        }
        this._type = value?.__typename;

        if (theme.mediaItem.h - this.h > 40) {
            this.Placeholder.patch({
                scale: 0.6,
                pivot: 0.5,
            });
        }

        this._update();
    }

    set useBorder(value: boolean) {
        this._useBorder = value;
    }

    set useBackground(value: boolean) {
        this._useBackground = value;

        if (this._useBackground) {
            const props = getMediaPosterProps(this._type);

            const color = Colors(theme.colors.bg.light).alpha(0.9).get();
            this.Background.patch({
                w: props.w,
                h: props.h,
                texture: Lightning.Tools.getRoundRect(props.w, props.h, props.radius, 0, undefined, true, color)
            });
        }
    }

    override _setup() {
        this._boundEventHandler = {
            show: this._showPoster.bind(this),
            hide: this._hidePoster.bind(this)
        };
    }

    private _onThemeChange(customTheme: Theme | undefined): void {
        if (this._useBorder) {
            const props = getMediaPosterProps(this._type);
            this.Border.patch({
                texture: Lightning.Tools.getRoundRect(
                    props.w,
                    props.h,
                    props.radius,
                    theme.mediaItem.border.w,
                    Colors(customTheme?.interaction ?? theme.colors.text.accent).get(),
                    false
                )
            });
            // Maintain the current alpha state
            this.Border.setSmooth("alpha", this.hasFocus() ? 1 : 0);
        }
    }

    override _detach() {
        themeManager.unsubscribe(this._themeCallback);
    }

    override _init() {
        // @ts-expect-error: types are missing
        if (theme.mediaColors && theme.mediaColors[this.parent?.parent?.componentIndex % 4]) {
            // @ts-expect-error: types are missing
            const color = Colors(theme.mediaColors[this.parent!.parent!.componentIndex % 4]).get();
            this.Background.patch({
                texture: Lightning.Tools.getRoundRect(
                    theme.mediaItem.w,
                    theme.mediaItem.h,
                    theme.mediaItem.border.radius,
                    0,
                    undefined,
                    true,
                    color
                )
            });
        }

        this._themeCallback = this._onThemeChange.bind(this);
        themeManager.subscribe(this._themeCallback);

        this.Background.patch({
            shader: { type: Lightning.shaders.RoundedRectangle, radius: theme.mediaItem.border.radius }
        });
    }

    override _active() {
        this.Poster.on("txLoaded", this._boundEventHandler.show);
        this.Poster.on("txError", this._boundEventHandler.hide);

        this._isActive = true;

        this._update();
    }

    override _inactive() {
        this.Poster.off("txLoaded", this._boundEventHandler.show);
        this.Poster.off("txError", this._boundEventHandler.hide);

        this._isActive = false;

        this._update();

        // this.stage.gc();
    }

    override _focus() {
        this._createBackground();
    }

    override _unfocus() {
        this._createBackground();
    }

    _createBackground() {
        if (this._useBorder) {
            if (!this.Border.texture) {
                const props = getMediaPosterProps(this._type);

                this.Border.patch({
                    texture: Lightning.Tools.getRoundRect(
                        props.w,
                        props.h,
                        props.radius,
                        props.borderW,
                        Colors(theme.colors.text.accent).get(),
                        false
                    )
                });
            }

            this.Border.setSmooth("alpha", this.hasFocus() ? 1 : 0);
        }
    }

    _showPoster() {
        this.Placeholder.setSmooth("alpha", 0);
        this.Poster.setSmooth("alpha", 1);
    }

    _hidePoster() {
        this.Placeholder.setSmooth("alpha", 1);
        this.Poster.setSmooth("alpha", 0.01);
    }

    _update() {
        if (this._isActive) {
            const { isChannel, ...props } = getMediaPosterProps(this._type);

            this._createBackground();

            if (this._src) {
                if (isChannel) {
                    this.Poster.patch({
                        w: undefined,
                        h: undefined,
                        texture: Img(`${this._src}?width=${props.posterWidth}&height=${props.h}&quality=80`).contain(
                            props.posterWidth,
                            props.posterHeight
                        )
                    });
                } else if(this._src?.startsWith('data:image/')) {
                    const size = Math.min(props.w, props.posterHeight);
                    this.Poster.patch({
                    w: size,
                    h: size,
                    mount: 0.5,
                    x: props.w / 2,
                    y: props.posterHeight / 2,
                    texture: Img(this._src).contain(size, size),
                    });
                } else {
                    this.Poster.patch({
                        w: props.w,
                        h: props.posterHeight,
                        src: `${this._src}${
                            configs.appId != "skyvod"
                                ? `?width=${props.w}&height=${props.h}&quality=80`
                                : `/${props.w}x${props.posterHeight}_fit.jpg`
                        }`
                    });
                    // Use a different scaling mode for kidjo to avoid over-zooming/cropping
                    const isKidjo = configs.appId === "kidjo";

                    if (isKidjo) {
                        // For kidjo, use undefined width/height to let contain() maintain aspect ratio
                        // This prevents stretching and ensures text in images isn't cropped
                        this.Poster.patch({
                            w: undefined,
                            h: undefined,
                            texture: Img(`${this._src}?width=${props.w}&quality=80`).contain(
                                props.w,
                                props.posterHeight
                            )
                        });
                    } else {
                        this.Poster.patch({
                            w: props.w,
                            h: props.posterHeight,
                            src: `${this._src}${
                                configs.appId != "skyvod"
                                    ? `?width=${props.w}&height=${props.h}&quality=80`
                                    : `/${props.w}x${props.posterHeight}_fit.jpg`
                            }`
                        });
                    }
                }
            } else {
                if (isChannel) {
                    this.Poster.patch({
                        texture: undefined
                    });
                    this.Placeholder.setSmooth("alpha", 1);
                    this.Poster.setSmooth("alpha", 0.01);
                }
            }
        }
    }
}
